<template>
  <div class="discipline-container">
    <!-- Hero Header Section -->
    <v-card class="hero-header mb-6" elevation="0">
      <div class="hero-content">
        <div class="hero-icon-wrapper">
          <v-icon size="48" color="white">mdi-shield-account</v-icon>
        </div>
        <div class="hero-text">
          <h1 class="hero-title">ประวัติเกี่ยวกับวินัยและโทษ</h1>
          <p class="hero-subtitle">ข้อมูลประวัติการถูกลงโทษทางวินัย</p>
        </div>
        <v-spacer></v-spacer>
        <div class="status-badge" :class="hasRecords ? 'has-records' : 'no-records'">
          <v-icon :color="hasRecords ? '#dc2626' : '#16a34a'" class="mr-2">
            {{ hasRecords ? 'mdi-alert-circle' : 'mdi-check-circle' }}
          </v-icon>
          <span>{{ hasRecords ? 'มีประวัติ' : 'ไม่มีประวัติ' }}</span>
        </div>
      </div>
    </v-card>

    <!-- Status Summary Cards -->
    <v-row class="stats-row mb-6">
      <v-col cols="12" sm="6" md="4">
        <v-card class="stat-card stat-card-1" elevation="0">
          <div class="stat-icon">
            <v-icon color="white" size="28">mdi-file-document-outline</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-number">{{ personnel_disciplines.length }}</span>
            <span class="stat-label">รายการทั้งหมด</span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="4">
        <v-card class="stat-card" :class="hasRecords ? 'stat-card-warning' : 'stat-card-success'" elevation="0">
          <div class="stat-icon">
            <v-icon color="white" size="28">
              {{ hasRecords ? 'mdi-alert-outline' : 'mdi-shield-check' }}
            </v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-number">{{ hasRecords ? 'มี' : 'ไม่มี' }}</span>
            <span class="stat-label">สถานะประวัติ</span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="4">
        <v-card class="stat-card stat-card-info" elevation="0">
          <div class="stat-icon">
            <v-icon color="white" size="28">mdi-calendar-check</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-number">{{ formattedCurrentDate }}</span>
            <span class="stat-label">วันที่ตรวจสอบ</span>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Clean Record Card (when no records) -->
    <v-card v-if="!hasRecords && !loading" class="clean-record-card mb-6" elevation="0">
      <div class="clean-record-content">
        <div class="clean-icon-wrapper">
          <v-icon size="80" color="#16a34a">mdi-shield-check</v-icon>
        </div>
        <div class="clean-text">
          <h2 class="clean-title">ไม่พบประวัติทางวินัย</h2>
          <p class="clean-desc">ท่านไม่มีประวัติการถูกลงโทษทางวินัยในระบบ</p>
        </div>
        <v-chip color="#dcfce7" text-color="#15803d" class="clean-badge" large>
          <v-icon left>mdi-check-decagram</v-icon>
          สถานะดี
        </v-chip>
      </div>
    </v-card>

    <!-- Main Data Table Card -->
    <v-card class="main-table-card" elevation="0" v-if="hasRecords || loading">
      <v-card-title class="table-header">
        <div class="header-left">
          <v-icon class="mr-3" color="#dc2626">mdi-format-list-bulleted</v-icon>
          <span class="header-title">รายการประวัติวินัย</span>
        </div>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="ค้นหารายการ..."
          single-line
          hide-details
          dense
          outlined
          class="search-field"
          style="max-width: 350px"
        ></v-text-field>
      </v-card-title>

      <v-divider></v-divider>

      <v-data-table
        :loading="loading"
        :headers="headers"
        :items="personnel_disciplines"
        :search="search"
        :items-per-page="10"
        class="modern-table"
        loading-text="กำลังโหลดข้อมูล..."
        no-data-text="ไม่พบข้อมูลประวัติวินัย"
        no-results-text="ไม่พบผลลัพธ์ที่ค้นหา"
      >
        <template v-slot:item="{ item, index }">
          <tr class="table-row">
            <td class="text-center">
              <v-avatar size="32" color="#dc2626" class="white--text font-weight-bold">
                {{ index + 1 }}
              </v-avatar>
            </td>
            <td>
              <div class="college-cell">
                <v-icon small color="#0ea5e9" class="mr-2">mdi-school</v-icon>
                <span>{{ item.college_name }}</span>
              </div>
            </td>
            <td class="text-center">
              <v-chip small color="#f1f5f9" text-color="#475569">
                {{ item.id_card }}
              </v-chip>
            </td>
            <td>
              <div class="name-cell">
                <v-avatar size="28" color="#fef2f2" class="mr-2">
                  <v-icon small color="#dc2626">mdi-account</v-icon>
                </v-avatar>
                <span>{{ item.title_s }}{{ item.frist_name }} {{ item.last_name }}</span>
              </div>
            </td>
            <td>
              <v-chip small color="#fef2f2" text-color="#dc2626" class="font-weight-medium">
                <v-icon left small>mdi-tag</v-icon>
                {{ item.discipline_blame }}
              </v-chip>
            </td>
            <td>
              <div class="detail-cell">
                <span class="detail-text">{{ item.location_disbla }}</span>
              </div>
            </td>
            <td class="text-center">
              <v-chip small color="#fff7ed" text-color="#ea580c">
                <v-icon left small>mdi-calendar</v-icon>
                {{ formatDate(item.disciplinedate_time) }}
              </v-chip>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>

    <!-- Information Card -->
    <v-card class="info-card mt-6" elevation="0">
      <v-expansion-panels flat class="info-panels">
        <v-expansion-panel>
          <v-expansion-panel-header class="info-header">
            <div class="d-flex align-center">
              <div class="info-icon-wrapper mr-3">
                <v-icon color="white" size="20">mdi-information-outline</v-icon>
              </div>
              <div>
                <div class="info-title">ข้อมูลเพิ่มเติมเกี่ยวกับวินัยข้าราชการ</div>
                <div class="info-subtitle">ระเบียบและข้อกำหนดที่เกี่ยวข้อง</div>
              </div>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="info-content">
            <v-row>
              <v-col cols="12" md="6">
                <div class="info-item">
                  <div class="info-item-header">
                    <v-icon color="#dc2626" class="mr-2">mdi-gavel</v-icon>
                    <span>ประเภทโทษทางวินัย</span>
                  </div>
                  <ul class="info-list">
                    <li><span class="bullet bullet-1"></span> ภาคทัณฑ์</li>
                    <li><span class="bullet bullet-2"></span> ตัดเงินเดือน</li>
                    <li><span class="bullet bullet-3"></span> ลดเงินเดือน</li>
                    <li><span class="bullet bullet-4"></span> ปลดออก</li>
                    <li><span class="bullet bullet-5"></span> ไล่ออก</li>
                  </ul>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="info-item">
                  <div class="info-item-header">
                    <v-icon color="#0ea5e9" class="mr-2">mdi-book-open-page-variant</v-icon>
                    <span>กฎหมายที่เกี่ยวข้อง</span>
                  </div>
                  <ul class="info-list">
                    <li><span class="bullet bullet-info"></span> พ.ร.บ. ระเบียบข้าราชการครู พ.ศ. 2547</li>
                    <li><span class="bullet bullet-info"></span> พ.ร.บ. ระเบียบข้าราชการพลเรือน พ.ศ. 2551</li>
                    <li><span class="bullet bullet-info"></span> ระเบียบ ก.ค.ศ. ว่าด้วยวินัยและการรักษาวินัย</li>
                  </ul>
                </div>
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>

            <div class="notice-box">
              <v-icon color="#f97316" class="mr-2">mdi-alert-circle-outline</v-icon>
              <div>
                <strong>หมายเหตุ:</strong> ข้อมูลในหน้านี้เป็นข้อมูลที่บันทึกในระบบเท่านั้น 
                หากต้องการข้อมูลอย่างเป็นทางการ กรุณาติดต่อฝ่ายบุคคลของหน่วยงาน
              </div>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :timeout="snackbar.timeout"
      :color="snackbar.color"
      top
      right
      class="modern-snackbar"
    >
      <div class="d-flex align-center">
        <v-icon class="mr-3" color="white">{{ snackbar.icon }}</v-icon>
        <span>{{ snackbar.text }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">ปิด</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  name: 'PersonnelDiscipline',

  data() {
    return {
      ApiKey: 'HRvec2021',
      loading: true,
      search: '',
      snackbar: {
        show: false,
        color: '',
        timeout: 4000,
        icon: '',
        text: ''
      },
      headers: [
        { text: '#', align: 'center', value: 'index', width: '60px', sortable: false },
        { text: 'สถานศึกษา', align: 'left', value: 'college_name' },
        { text: 'รหัสบัตร', align: 'center', value: 'id_card', width: '150px' },
        { text: 'ชื่อ-นามสกุล', align: 'left', value: 'frist_name' },
        { text: 'หัวข้อ', align: 'left', value: 'discipline_blame', width: '180px' },
        { text: 'รายละเอียด', align: 'left', value: 'location_disbla' },
        { text: 'วันที่', align: 'center', value: 'disciplinedate_time', width: '140px' }
      ],
      personnel_disciplines: []
    }
  },

  computed: {
    hasRecords() {
      return this.personnel_disciplines && this.personnel_disciplines.length > 0
    },
    formattedCurrentDate() {
      return new Date().toLocaleDateString('th-TH', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    }
  },

  async mounted() {
    await this.personnel_disciplinesQueryAll()
  },

  methods: {
    async personnel_disciplinesQueryAll() {
      this.loading = true
      let userSession = JSON.parse(localStorage.getItem('user')) || 0
      let result = await this.$http
        .post('personnel_discipline.php', {
          ApiKey: this.ApiKey,
          disciplineid_card: userSession.id_card
        })
        .finally(() => (this.loading = false))
      this.personnel_disciplines = result.data || []
    },

    formatDate(dateString) {
      if (!dateString) return 'ไม่ระบุ'
      try {
        const date = new Date(dateString)
        return date.toLocaleDateString('th-TH', {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        })
      } catch {
        return dateString
      }
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Prompt:wght@300;400;500;600;700&display=swap');

.discipline-container {
  font-family: 'Prompt', sans-serif;
  padding: 24px;
  background: linear-gradient(180deg, #fafafa 0%, #f5f5f5 100%);
  min-height: 100vh;
}

/* Hero Header */
.hero-header {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 50%, #991b1b 100%);
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 10px 40px rgba(220, 38, 38, 0.3);
}

.hero-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.06'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.hero-content {
  display: flex;
  align-items: center;
  padding: 32px;
  position: relative;
  z-index: 1;
  flex-wrap: wrap;
  gap: 16px;
}

.hero-icon-wrapper {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.hero-text {
  color: white;
}

.hero-title {
  font-size: 26px;
  font-weight: 600;
  margin: 0 0 6px 0;
  letter-spacing: -0.3px;
}

.hero-subtitle {
  font-size: 14px;
  opacity: 0.9;
  margin: 0;
  font-weight: 300;
}

.status-badge {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  border-radius: 12px;
  font-weight: 500;
  font-size: 15px;
}

.status-badge.no-records {
  background: rgba(255, 255, 255, 0.95);
  color: #16a34a;
}

.status-badge.has-records {
  background: rgba(255, 255, 255, 0.95);
  color: #dc2626;
}

/* Statistics Cards */
.stats-row {
  margin-top: -16px;
}

.stat-card {
  background: white;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);
}

.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-card-1 .stat-icon { background: linear-gradient(135deg, #64748b 0%, #475569 100%); }
.stat-card-success .stat-icon { background: linear-gradient(135deg, #16a34a 0%, #15803d 100%); }
.stat-card-warning .stat-icon { background: linear-gradient(135deg, #f97316 0%, #ea580c 100%); }
.stat-card-info .stat-icon { background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%); }

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-number {
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1;
}

.stat-label {
  font-size: 13px;
  color: #6b7280;
  margin-top: 4px;
  font-weight: 400;
}

/* Clean Record Card */
.clean-record-card {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border-radius: 20px;
  border: 2px solid #bbf7d0;
  overflow: hidden;
}

.clean-record-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 32px;
  text-align: center;
}

.clean-icon-wrapper {
  width: 120px;
  height: 120px;
  background: white;
  border-radius: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  box-shadow: 0 8px 32px rgba(22, 163, 74, 0.2);
}

.clean-title {
  font-size: 24px;
  font-weight: 600;
  color: #166534;
  margin: 0 0 8px 0;
}

.clean-desc {
  font-size: 15px;
  color: #4ade80;
  margin: 0 0 20px 0;
  font-weight: 400;
}

.clean-badge {
  font-weight: 600;
  padding: 0 24px;
  height: 40px;
}

/* Main Table Card */
.main-table-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
}

.table-header {
  padding: 20px 24px;
  background: #fafafa;
}

.header-left {
  display: flex;
  align-items: center;
}

.header-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
}

.search-field {
  background: white;
}

.search-field >>> .v-input__slot {
  border-radius: 12px !important;
}

/* Data Table Styles */
.modern-table {
  font-family: 'Prompt', sans-serif;
}

.modern-table >>> thead th {
  background: #fef2f2 !important;
  color: #991b1b !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border-bottom: 2px solid #fecaca !important;
  padding: 16px 12px !important;
}

.table-row {
  transition: all 0.2s ease;
}

.table-row:hover {
  background: #fffbfb !important;
}

.table-row td {
  padding: 16px 12px !important;
  border-bottom: 1px solid #f3f4f6 !important;
  font-size: 14px;
  color: #374151;
}

.college-cell {
  display: flex;
  align-items: center;
}

.name-cell {
  display: flex;
  align-items: center;
}

.detail-cell {
  max-width: 250px;
}

.detail-text {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.5;
}

/* Info Card */
.info-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.04);
}

.info-panels {
  background: transparent !important;
}

.info-panels >>> .v-expansion-panel {
  background: transparent !important;
}

.info-panels >>> .v-expansion-panel::before {
  box-shadow: none !important;
}

.info-header {
  padding: 20px 24px !important;
}

.info-icon-wrapper {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.info-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}

.info-subtitle {
  font-size: 12px;
  color: #6b7280;
}

.info-content {
  padding: 0 24px 24px !important;
}

.info-item {
  background: #fafafa;
  border-radius: 14px;
  padding: 20px;
  height: 100%;
}

.info-item-header {
  display: flex;
  align-items: center;
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 16px;
}

.info-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.info-list li {
  display: flex;
  align-items: center;
  padding: 8px 0;
  font-size: 14px;
  color: #4b5563;
}

.bullet {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 12px;
  flex-shrink: 0;
}

.bullet-1 { background: #fbbf24; }
.bullet-2 { background: #fb923c; }
.bullet-3 { background: #f97316; }
.bullet-4 { background: #ef4444; }
.bullet-5 { background: #dc2626; }
.bullet-info { background: #0ea5e9; }

.notice-box {
  display: flex;
  align-items: flex-start;
  background: #fffbeb;
  border: 1px solid #fef3c7;
  border-radius: 12px;
  padding: 16px;
  font-size: 13px;
  color: #92400e;
}

.notice-box strong {
  color: #78350f;
}

/* Snackbar */
.modern-snackbar >>> .v-snack__wrapper {
  border-radius: 12px;
  font-family: 'Prompt', sans-serif;
}

/* Responsive */
@media (max-width: 960px) {
  .hero-content {
    padding: 24px;
  }

  .hero-icon-wrapper {
    width: 64px;
    height: 64px;
  }

  .hero-title {
    font-size: 20px;
  }

  .status-badge {
    width: 100%;
    justify-content: center;
  }

  .detail-cell {
    max-width: 150px;
  }
}

@media (max-width: 600px) {
  .discipline-container {
    padding: 16px;
  }

  .hero-content {
    padding: 20px;
  }

  .stat-card {
    padding: 16px;
  }

  .stat-number {
    font-size: 20px;
  }

  .clean-record-content {
    padding: 32px 20px;
  }

  .clean-icon-wrapper {
    width: 100px;
    height: 100px;
  }

  .clean-title {
    font-size: 20px;
  }
}

/* Animation */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stat-card {
  animation: fadeInUp 0.5s ease forwards;
  opacity: 0;
}

.stat-card:nth-child(1) { animation-delay: 0.1s; }
.stat-card:nth-child(2) { animation-delay: 0.2s; }
.stat-card:nth-child(3) { animation-delay: 0.3s; }

.clean-record-card {
  animation: fadeInUp 0.6s ease forwards;
  animation-delay: 0.2s;
  opacity: 0;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.clean-icon-wrapper {
  animation: pulse 2s ease-in-out infinite;
}
</style>